<script setup lang="ts">
  import { defaultMessageDurationSeconds } from '@/utils'
  import { DataCleaningService } from '../services/DataCleaningService'
  import { Button, message } from 'ant-design-vue'
  import {
    CopyOutlined,
    DownloadOutlined,
    EditOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UploadOutlined
  } from '@ant-design/icons-vue'
  import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    reactive,
    ref,
    toRef,
    watchEffect
  } from 'vue'
  import { useColorMode } from '@vueuse/core'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import JSONEditor from 'jsoneditor'
  import { DataCleaningJsonParsingService } from '../services/DataCleaningJsonParsingService'
  import { debounceTime, Subject } from 'rxjs'
  import { RuleSchema } from '../models/RuleSchema'
  import { Rule } from '../models/Rule'

  interface IRuleJsonManagementProps {
    ruleSchemas: Array<RuleSchema>
    fileId: string
    rules: Array<Rule>
    outputFileName: string
    visibilityChanged: boolean
    dataCleaningJsonParsingService: DataCleaningJsonParsingService
  }

  interface IRuleJsonManagementEmits {
    (e: 'rules-applied'): void
  }

  const props = defineProps<IRuleJsonManagementProps>()
  const emit = defineEmits<IRuleJsonManagementEmits>()

  // Do not propagate rule changes to parent until user-applied

  const fileId = toRef(props, 'fileId')
  const outputFileName = toRef(props, 'outputFileName')
  const nonAppliedRules = reactive<Array<Rule>>([...props.rules])
  const activeRules = reactive<Array<Rule>>(props.rules)
  const activeView = ref<'view' | 'edit'>('view')
  const editorHasChanges = ref<boolean>(false)

  const formattedRulesJson = ref<any>()
  const dependencyChange$ = new Subject<void>()

  dependencyChange$.pipe(debounceTime(300)).subscribe(() => {
    formattedRulesJson.value = DataCleaningService.getFormattedRulesJson(
      props.fileId,
      activeRules,
      props.outputFileName
    )
  })

  watchEffect(() => {
    props.fileId
    props.outputFileName
    activeRules
    dependencyChange$.next()
  })

  const importedJson = ref<string | undefined>()

  const colorMode = useColorMode()
  const isDarkMode = computed(() => (colorMode.value === 'dark' ? true : false))

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const toggleViewMode = () => {
    activeView.value = activeView.value === 'view' ? 'edit' : 'view'

    nextTick(() => {
      if (activeView.value === 'edit') {
        initializeJSONEditor()
      } else {
        destroyEditor()
      }
    })
  }

  const destroyEditor = () => {
    if (editor) {
      editor.destroy()
    }
  }

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        DataCleaningService.getFormattedRulesJsonString(
          props.fileId,
          activeRules,
          props.outputFileName
        )
      )
      message.success('JSON copied to clipboard!', defaultMessageDurationSeconds)
    } catch (error) {
      message.error('Failed to copy to clipboard', defaultMessageDurationSeconds)
    }
  }

  // Ref for the hidden file input
  const fileInput = ref<HTMLInputElement | null>(null)

  const downloadJsonFile = () => {
    const jsonContent = DataCleaningService.getFormattedRulesJsonString(
      fileId.value,
      activeRules,
      outputFileName.value
    )
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `data-cleaning-rules-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    message.success('JSON file downloaded!', defaultMessageDurationSeconds)
  }

  const importJsonFile = () => {
    // Reset JSON Editor
    nonAppliedRules.splice(0, nonAppliedRules.length)
    setJSONEditorModel(fileId.value, nonAppliedRules, outputFileName.value)
    editorHasChanges.value = true
    fileInput.value?.click()
  }

  const handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (file) {
      // Optional: validate file type
      if (!file.name.endsWith('.json')) {
        message.error('Please select a valid .json file', defaultMessageDurationSeconds)
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        importedJson.value = reader.result as string
        const result = updateNonAppliedRulesFromJsonString(importedJson.value)
        if (result === true)
          message.success('JSON parsed successfully.', defaultMessageDurationSeconds)
      }
      reader.readAsText(file)
    }

    // Reset input value so the same file can be selected again later
    input.value = ''
  }

  const updateNonAppliedRulesFromJsonString = (json: string): boolean => {
    if (json == null) throw new Error('valid json required')
    const result = props.dataCleaningJsonParsingService.tryParseEntitiesFromJson(
      json,
      props.ruleSchemas
    )
    if (!result.valid) {
      message.error(
        `Error parsing JSON: ${result.errors?.join(', ')}`,
        defaultMessageDurationSeconds
      )
      return false
    } else {
      // Passes validation should expect rules to exist
      nonAppliedRules.splice(0, nonAppliedRules.length, ...(result.parsedEntities as Array<Rule>))
      setJSONEditorModel(fileId.value, nonAppliedRules, outputFileName.value)
      return true
    }
  }

  const applyJsonRules = () => {
    try {
      const updatedPayloadString = editor?.getText()
      const result = updateNonAppliedRulesFromJsonString(updatedPayloadString)
      if (result === true) {
        message.success('JSON parsed and applied successfully.', defaultMessageDurationSeconds)
        activeRules.splice(0, activeRules.length, ...nonAppliedRules)
        editorHasChanges.value = false
      }
      emit('rules-applied')
    } catch (e) {
      message.error(`Error getting payload value from JSONEditor`, defaultMessageDurationSeconds)
    }
  }

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editorHasChanges.value = false
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          editorHasChanges.value = true
        }
      })
      setJSONEditorModel(fileId.value, activeRules, outputFileName.value)
    }
  }

  const setJSONEditorModel = (fileId: string, rules: Array<Rule>, outputFileName: string) => {
    if (editor != null && editorContainer.value != null) {
      const json = DataCleaningService.getFormattedRulesJson(fileId, rules, outputFileName)
      editor.set(json)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  onMounted(() => {
    initializeJSONEditor()
    formattedRulesJson.value = DataCleaningService.getFormattedRulesJson(
      props.fileId,
      activeRules,
      props.outputFileName
    )
  })

  onUnmounted(() => {
    destroyEditor()
  })
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Hidden file input -->
    <input type="file" ref="fileInput" accept=".json" class="hidden" @change="handleFileSelected" />

    <!-- Header Actions -->
    <div class="mb-4 flex justify-end gap-2">
      <a-tooltip placement="bottom" v-if="activeView === 'view'">
        <template #title>{{ 'Edit or Import JSON' }}</template>
        <Button class="flex items-center" type="primary" @click="toggleViewMode">
          <EditOutlined />
          {{ 'Edit JSON' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom" v-else-if="activeView === 'edit'">
        <template #title>
          {{ editorHasChanges === true ? 'All changes will be lost.' : 'Select to view JSON.' }}
        </template>
        <Button
          :class="['flex items-center', { warning: editorHasChanges === true }]"
          @click="toggleViewMode"
        >
          <SearchOutlined />
          {{ 'View Only' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Copy Applied JSON to clipboard</template>
        <Button class="flex items-center" @click="copyJsonToClipboard">
          <CopyOutlined />
          Copy to Clipboard
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Download Applied JSON</template>
        <Button class="flex items-center" @click="downloadJsonFile">
          <DownloadOutlined />
          Download JSON
        </Button>
      </a-tooltip>

      <Button class="flex items-center" :disabled="activeView !== 'edit'" @click="importJsonFile">
        <UploadOutlined />
        Import JSON
      </Button>
      <Button
        type="primary"
        class="flex items-center"
        :disabled="activeView === 'view' || editorHasChanges === false"
        @click="applyJsonRules"
      >
        <PlayCircleOutlined />
        Apply
      </Button>
    </div>

    <!-- JSON Content -->
    <div class="flex-1">
      <div class="relative z-[10] flex"></div>
      <div class="z-[1] mt-[10px]">
        <!-- View -->
        <div
          v-if="activeView === 'view'"
          class="overflow-auto rounded-sm border bg-card p-1"
          style="max-height: calc(100vh - 300px)"
        >
          <div :class="['viewer-container', 'json-pretty-wrapper', { 'dark-mode': isDarkMode }]">
            <vue-json-pretty :data="formattedRulesJson as any" />
          </div>
        </div>
        <!-- Edit -->
        <div class="editor-container" v-else-if="activeView === 'edit'" ref="editorContainer"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .editor-container {
    height: calc(100vh - 165px);
  }
  .viewer-container {
    min-height: calc(100vh - 165px);
    height: 100%;
  }
</style>
